// // Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { usePoolOverview } from '@react/hooks'
import { useOtChainState } from '@react/redux/store'
import { erc20WeiToCommaDecString } from '@shared/conversions'
import React, { useState, useEffect } from 'react'

import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import { useTheme } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import Accordion from '@mui/material/Accordion'
import AccordionSummary from '@mui/material/AccordionSummary'
import AccordionDetails from '@mui/material/AccordionDetails'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { LineChart } from '@mui/x-charts/LineChart'
import { Select, MenuItem, FormControl, Link } from '@mui/material'
import { EthAddr } from '@shared/SchemaTypes'
import { TransparencyVariant } from '@shared/Enums'
import { ethers } from 'ethers'
import { Live24hVolume } from './Lyt24HVolume'
import { LytHistorical } from './LytHistorical'
import { LytReserves } from './LytReserves'
import { LytReserveReports } from './LytReserveReports'

import { LytHistoryPeriod, LytHistoryCTO } from './LytFetchers'
import { fetchDailyPoolOverviewCTOs } from '@react/fetchers/PoolFetchers'
import { getDexService } from './dexService'
import { getSwapTokenConfig } from '@shared/SwapTokenConfig'
import { useAPYData, useReserves } from '@react/hooks/useCurrentAPY'
import { useOtDispatch } from '@react/redux/store'
import { clearReservesCache } from '@react/redux/apySlice'

export function LytTransparency(props: { poolAddr?: EthAddr; variant?: TransparencyVariant }): React.ReactElement {
  const theme = useTheme()
  const [expandedFaq, setExpandedFaq] = useState<string | false>(false)
  const {
    stageConfiguration: { restAPIKey, serverApiBaseUrl, lytPoolAddr, stageName },
    chainConfiguration,
    userConnection: { accountAddr },
  } = useOtChainState()

  // Use the prop if provided, otherwise fallback to Redux
  const dispatch = useOtDispatch()
  const poolAddr = props.poolAddr ?? lytPoolAddr
  const variant = props.variant ?? TransparencyVariant.ADMIN
  const isApp = variant === TransparencyVariant.APP

  // Force refresh reserves cache to pick up yield type changes
  useEffect(() => {
    if (isApp) {
      console.warn('Clearing reserves cache to force refresh...')
      dispatch(clearReservesCache())
    }
  }, [isApp, dispatch])

  const [refreshKey, _setRefreshKey] = useState(0)
  const [activeTab, setActiveTab] = useState<'apy' | 'asset' | 'exchange'>('apy')
  const [timeFilter, setTimeFilter] = useState<'1M' | '3M' | '6M' | '1Y'>('1M')
  const [selectedAsset, setSelectedAsset] = useState<string>('')
  const [_historicalApyData, _setHistoricalApyData] = useState<LytHistoryCTO[]>([])
  const [_historicalExchangeRateData, _setHistoricalExchangeRateData] = useState<LytHistoryCTO[]>([])
  const [localApyGraphData, setLocalApyGraphData] = useState<
    Array<{ timestamp: number; value: number; month: string }>
  >([])
  const [assetApyGraphData, setAssetApyGraphData] = useState<
    Array<{ timestamp: number; value: number; month: string }>
  >([])
  const [exchangeRateGraphData, setExchangeRateGraphData] = useState<
    Array<{ timestamp: number; value: number; month: string }>
  >([])
  const [_historicalLoading, _setHistoricalLoading] = useState(false)
  const [assetApyLoading, setAssetApyLoading] = useState(true)
  const [exchangeRateLoading, setExchangeRateLoading] = useState(true)
  const [assetApyData, setAssetApyData] = useState<
    Record<string, Array<{ timestamp: number; value: number; month: string }>>
  >({})
  const [exchangeRateData, setExchangeRateData] = useState<
    Record<string, Array<{ timestamp: number; value: number; month: string }>>
  >({})
  const [marketPrice, setMarketPrice] = useState<string | null>(null)

  const {
    currentAPY: reduxCurrentAPY,
    graphData: reduxGraphData,
    isLoading: reduxLoading,
  } = useAPYData({
    enabled: isApp,
    timeFilter,
  })

  const { reserves: reduxReserves, reservesWithRates } = useReserves({
    enabled: isApp,
  })

  console.warn('Transparency Redux Debug:', {
    reduxReservesLength: reduxReserves.length,
    reservesWithRatesLength: reservesWithRates.length,
    usdcReserveInRedux: reduxReserves.find((r) => r.poolName === 'USDC'),
    usdcReserveWithRates: reservesWithRates.find(
      (r) => r.poolName === 'USDC' || (r as any).displayName?.includes('Liquidity Buffer')
    ),
  })

  const { overview, loading } = usePoolOverview(serverApiBaseUrl, restAPIKey, poolAddr, refreshKey)
  const [currentWeightedAPY, setCurrentWeightedAPY] = useState<string | null>(null)

  const [localApyLoading, setLocalApyLoading] = useState(true)

  const apy = isApp ? reduxCurrentAPY : localApyLoading ? null : currentWeightedAPY
  const rawGraphData = isApp ? reduxGraphData : localApyGraphData
  const displayGraphData = rawGraphData.filter((_d, index) => {
    const firstNonZeroIndex = rawGraphData.findIndex((item) => item.value > 0)
    return index >= firstNonZeroIndex && firstNonZeroIndex !== -1
  })
  const displayApyLoading = isApp ? reduxLoading : localApyLoading
  const tvl = overview?.totalAssets !== undefined ? erc20WeiToCommaDecString(overview.totalAssets) : null
  const marketCap = (() => {
    if (overview?.totalShares !== undefined && overview?.exchangeRate !== undefined) {
      const totalSharesFormatted = Number(overview.totalShares) / 1e6
      const exchangeRateFormatted = Number(overview.exchangeRate) / 1e18
      const calculatedMarketCap = totalSharesFormatted * exchangeRateFormatted
      return `$${Math.round(calculatedMarketCap).toLocaleString()}`
    }
    return null
  })()
  const tokenPrice =
    marketPrice ||
    (overview?.exchangeRate !== undefined ? `$${(1e18 / Number(overview.exchangeRate)).toFixed(6)}` : null)
  const creationTimestamp = overview?.creationTimestamp ?? null

  const providerReadOnly = new ethers.JsonRpcProvider(chainConfiguration.frontendProviderUrl)

  useEffect(() => {
    const fetchMarketPrice = async () => {
      if (!serverApiBaseUrl || !restAPIKey || !isApp) return

      try {
        const swapTokenConfig = getSwapTokenConfig(stageName, chainConfiguration.chainId)
        const useOkx = true
        const swapServices = getDexService(
          useOkx,
          stageName,
          chainConfiguration.chainId,
          providerReadOnly,
          serverApiBaseUrl,
          restAPIKey,
          chainConfiguration.frontendProviderUrl
        )
        const quoteRequest = {
          fromTokenAddress: swapTokenConfig.TOKEN_B.address,
          toTokenAddress: swapTokenConfig.TOKEN_A.address,
          amount: '1',
          slippage: '0.5',
          userWalletAddress: '0x0000000000000000000000000000000000000000',
          autoSlippage: true,
          maxAutoSlippagePercent: '10',
        }

        const quoteResponse = await swapServices.getQuote(quoteRequest)
        if (quoteResponse.fromAmount && quoteResponse.toAmount) {
          const fromDecimals = (quoteResponse as any).protocols?.[0]?.subRouterList?.[0]?.fromToken?.decimal || 6
          const toDecimals = (quoteResponse as any).protocols?.[0]?.subRouterList?.[0]?.toToken?.decimal || 6

          const fromAmount = parseFloat(quoteResponse.fromAmount) / Math.pow(10, fromDecimals)
          const toAmount = parseFloat(quoteResponse.toAmount) / Math.pow(10, toDecimals)

          const sierraToUsdcRate = toAmount / fromAmount
          setMarketPrice(`${sierraToUsdcRate.toFixed(6)}`)
        }
      } catch (error) {
        console.warn('Failed to fetch market price:', error)
        setMarketPrice('1.000000')
      }
    }

    fetchMarketPrice()
  }, [serverApiBaseUrl, restAPIKey, chainConfiguration.chainId, isApp])

  const reserves = isApp ? reduxReserves : []

  useEffect(() => {
    if (!isApp || reserves.length === 0) {
      return
    }

    if (!selectedAsset && reserves.length > 0) {
      const totalValue = reserves.reduce((sum, reserve) => {
        const value = reserve.currentAssetValue
          ? (parseFloat(reserve.currentAssetValue) / 1e6) * 1e3
          : parseFloat(erc20WeiToCommaDecString(reserve.assetBalance))
        return sum + value
      }, 0)
      const sortedReserves = reserves
        .map((reserve) => {
          const assetValue = reserve.currentAssetValue
            ? (parseFloat(reserve.currentAssetValue) / 1e6) * 1e3
            : parseFloat(erc20WeiToCommaDecString(reserve.assetBalance))
          const percentage = totalValue > 0 ? (assetValue / totalValue) * 100 : 0
          return {
            ...reserve,
            percentage,
          }
        })
        .sort((a, b) => b.percentage - a.percentage)

      // Find the first valid reserve with a display name that's not USDC and not blank
      if (reservesWithRates.length > 0) {
        const validReserve = reservesWithRates.find(
          (r) => r.displayName && r.displayName.trim() !== '' && r.displayName !== 'USDC' && r.poolName !== 'USDC'
        )
        if (validReserve) {
          setSelectedAsset(validReserve.displayName!)
        } else {
          // Fallback to first non-USDC reserve
          const fallbackReserve = sortedReserves.find((r) => r.poolName !== 'USDC' && r.poolName?.trim())
          if (fallbackReserve) {
            setSelectedAsset(fallbackReserve.poolName)
          }
        }
      } else {
        // Fallback to first non-USDC reserve
        const fallbackReserve = sortedReserves.find((r) => r.poolName !== 'USDC' && r.poolName?.trim())
        if (fallbackReserve) {
          setSelectedAsset(fallbackReserve.poolName)
        }
      }
    }
  }, [reserves, isApp, selectedAsset, reservesWithRates])

  const totalAssetValue = (reserves || []).reduce((sum, reserve) => {
    const value = reserve.currentAssetValue
      ? (parseFloat(reserve.currentAssetValue) / 1e6) * 1e3
      : parseFloat(erc20WeiToCommaDecString(reserve.assetBalance))
    return sum + value
  }, 0)

  const aggregateToDaily = (rawData: Array<{ timestamp: number; value: number }>) => {
    if (rawData.length === 0) return []

    const dailyGroups: Record<string, Array<{ timestamp: number; value: number }>> = {}

    rawData.forEach((item) => {
      const dayKey = new Date(item.timestamp * 1000).toISOString().slice(0, 10)
      if (!dailyGroups[dayKey]) dailyGroups[dayKey] = []
      dailyGroups[dayKey].push(item)
    })

    return Object.entries(dailyGroups)
      .map(([_, dayItems]) => {
        const sortedItems = dayItems.sort((a, b) => b.timestamp - a.timestamp)
        return sortedItems[0]
      })
      .sort((a, b) => a.timestamp - b.timestamp)
  }

  const aggregateToWeekly = (dailyData: Array<{ timestamp: number; value: number }>) => {
    if (dailyData.length === 0) return []

    const weeklyData: Array<{ timestamp: number; value: number; month: string }> = []
    const WEEK_IN_SECONDS = 7 * 24 * 60 * 60

    const sortedData = [...dailyData].sort((a, b) => a.timestamp - b.timestamp)

    const now = new Date()
    const todayTimestamp = Math.floor(new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime() / 1000)

    const findClosestDataBefore = (targetTimestamp: number) => {
      let closestData = null
      let closestDistance = Infinity

      for (const item of sortedData) {
        if (item.timestamp <= targetTimestamp) {
          const distance = targetTimestamp - item.timestamp
          if (distance < closestDistance && !isNaN(item.value) && item.value !== null) {
            closestDistance = distance
            closestData = item
          }
        }
      }
      return closestData
    }

    let currentTargetDate = todayTimestamp
    const earliestTimestamp = sortedData[0].timestamp

    while (currentTargetDate >= earliestTimestamp) {
      const closestData = findClosestDataBefore(currentTargetDate)

      if (closestData) {
        weeklyData.push({
          timestamp: currentTargetDate,
          value: closestData.value,
          month: new Date(currentTargetDate * 1000).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
          }),
        })
      }

      currentTargetDate -= WEEK_IN_SECONDS
    }

    return weeklyData.sort((a, b) => a.timestamp - b.timestamp)
  }

  const reserveAssetsWithPercentage = (reserves || [])
    .filter((reserve) => reserve.poolName !== 'USDC' && reserve.poolName?.trim())
    .filter((reserve) => {
      const displayName = reservesWithRates.find((r) => r.poolAddr === reserve.poolAddr)?.displayName
      return displayName && displayName.trim() && displayName !== 'USDC'
    })
    .map((reserve) => {
      const assetValue = reserve.currentAssetValue
        ? (parseFloat(reserve.currentAssetValue) / 1e6) * 1e3
        : parseFloat(erc20WeiToCommaDecString(reserve.assetBalance))
      const percentage = totalAssetValue > 0 ? (assetValue / totalAssetValue) * 100 : 0
      return {
        name: (() => {
          const matchingReserve = reservesWithRates.find((r) => r.poolAddr === reserve.poolAddr)
          const finalName = matchingReserve?.displayName || reserve.poolName
          return finalName
        })(),
        percentage,
        apy: reserve.apy,
        poolAddr: reserve.poolAddr,
      }
    })
    .sort((a, b) => b.percentage - a.percentage)

  // APY Tab (Weighted Average)
  useEffect(() => {
    if (!serverApiBaseUrl || !restAPIKey || !poolAddr) {
      return
    }

    const loadApyData = async () => {
      if (isApp) {
        return
      }

      if (!reserves || reserves.length === 0) {
        return
      }

      setLocalApyLoading(true)

      try {
        const period = timeFilter as LytHistoryPeriod
        const start = new Date()
        switch (period) {
          case '1M':
            start.setMonth(start.getMonth() - 1)
            break
          case '3M':
            start.setMonth(start.getMonth() - 3)
            break
          case '6M':
            start.setMonth(start.getMonth() - 6)
            break
          case '1Y':
            start.setFullYear(start.getFullYear() - 1)
            break
          default:
            start.setMonth(start.getMonth() - 1)
        }
        const startDayStr = start.toISOString().slice(0, 10)

        const allPromises = reserves.map((reserve) =>
          fetchDailyPoolOverviewCTOs(serverApiBaseUrl, restAPIKey, reserve.poolAddr, startDayStr, undefined)
            .then((result) =>
              result.map((d: any) => ({
                assetValue: parseFloat(erc20WeiToCommaDecString(reserve.assetBalance)),
                timestamp: d.timestamp,
                apy: d.indicativeInterestRate
                  ? Number(d.indicativeInterestRate) / 100
                  : d.interestRate
                    ? Number(d.interestRate) / 100
                    : 0,
              }))
            )
            .catch(() => [])
        )

        const allAssetHistoricalData = await Promise.all(allPromises)
        const flatData = allAssetHistoricalData.flat()

        const groupedByTimestamp = flatData.reduce(
          (acc, item) => {
            if (!acc[item.timestamp]) acc[item.timestamp] = []
            acc[item.timestamp].push(item)
            return acc
          },
          {} as Record<number, Array<{ assetValue: number; timestamp: number; apy: number }>>
        )

        const weightedAverageData = Object.entries(groupedByTimestamp)
          .map(([timestamp, assets]) => {
            const totalValue = assets.reduce((sum, asset) => sum + asset.assetValue, 0)
            const weightedSum = assets.reduce((sum, asset) => sum + asset.apy * asset.assetValue, 0)
            const weightedAverage = totalValue > 0 ? weightedSum / totalValue : 0

            return { timestamp: parseInt(timestamp), value: weightedAverage }
          })
          .sort((a, b) => a.timestamp - b.timestamp)

        const dailyData = aggregateToDaily(weightedAverageData)
        const weeklyData = aggregateToWeekly(dailyData)
        setLocalApyGraphData(weeklyData)

        if (weeklyData.length > 0) {
          const latestGraphAPY = weeklyData[weeklyData.length - 1].value
          setCurrentWeightedAPY(latestGraphAPY.toFixed(2))
        }
      } catch (error) {
        console.error('Error loading APY data:', error)
      }
      setLocalApyLoading(false)
    }

    if (activeTab === 'apy') {
      loadApyData()
    }
  }, [activeTab, timeFilter, reserves, isApp, serverApiBaseUrl, restAPIKey, poolAddr, accountAddr])

  // Reserves APY Tab (Individual Assets)
  useEffect(() => {
    if (!isApp || !serverApiBaseUrl || !restAPIKey || !poolAddr || reserves.length === 0) {
      return
    }

    const loadAssetApyData = async () => {
      if (activeTab !== 'asset') return

      setAssetApyLoading(true)
      const targetAsset = selectedAsset || reserves[0].poolName
      const cacheKey = `${targetAsset}-${timeFilter}`

      if (assetApyData[cacheKey] && assetApyData[cacheKey].length > 0) {
        setAssetApyGraphData(assetApyData[cacheKey])
        setAssetApyLoading(false)
        return
      }

      try {
        const period = timeFilter as LytHistoryPeriod
        const start = new Date()
        switch (period) {
          case '1M':
            start.setMonth(start.getMonth() - 1)
            break
          case '3M':
            start.setMonth(start.getMonth() - 3)
            break
          case '6M':
            start.setMonth(start.getMonth() - 6)
            break
          case '1Y':
            start.setFullYear(start.getFullYear() - 1)
            break
          default:
            start.setMonth(start.getMonth() - 1)
        }
        const startDayStr = start.toISOString().slice(0, 10)

        // Try to find reserve by display name first, then by pool name
        let selectedReserve = reservesWithRates.find((r) => r.displayName === targetAsset)
        if (!selectedReserve) {
          selectedReserve = reserves.find((r) => r.poolName === targetAsset)
        }
        if (selectedReserve) {
          const result = await fetchDailyPoolOverviewCTOs(
            serverApiBaseUrl,
            restAPIKey,
            selectedReserve.poolAddr,
            startDayStr,
            undefined
          )

          const dailyData = result
            .map((d: any) => ({
              timestamp: d.timestamp,
              value: d.indicativeInterestRate
                ? Number(d.indicativeInterestRate) / 100
                : d.interestRate
                  ? Number(d.interestRate) / 100
                  : 0,
            }))
            .filter((item) => item.timestamp && item.value !== null && item.value !== undefined && !isNaN(item.value))
            .sort((a, b) => a.timestamp - b.timestamp)

          const singleDailyData = aggregateToDaily(dailyData)
          const weeklyData = aggregateToWeekly(singleDailyData)
          setAssetApyData((prev) => ({ ...prev, [cacheKey]: weeklyData }))
          setAssetApyGraphData(weeklyData)
        }
      } catch (error) {
        console.error('Error loading asset APY data:', error)
        setAssetApyGraphData([])
      }
      setAssetApyLoading(false)
    }

    loadAssetApyData()
  }, [activeTab, selectedAsset, timeFilter, reserves, isApp, serverApiBaseUrl, restAPIKey, poolAddr])

  // Exchange Rate Tab (Main LYT Pool)
  useEffect(() => {
    if (!isApp || !serverApiBaseUrl || !restAPIKey || !poolAddr) {
      return
    }

    const loadExchangeRateData = async () => {
      if (activeTab !== 'exchange') return

      setExchangeRateLoading(true)
      const cacheKey = `lyt-pool-${timeFilter}`

      if (exchangeRateData[cacheKey] && exchangeRateData[cacheKey].length > 0) {
        setExchangeRateGraphData(exchangeRateData[cacheKey])
        setExchangeRateLoading(false)
        return
      }

      try {
        const period = timeFilter as LytHistoryPeriod
        const start = new Date()
        switch (period) {
          case '1M':
            start.setMonth(start.getMonth() - 1)
            break
          case '3M':
            start.setMonth(start.getMonth() - 3)
            break
          case '6M':
            start.setMonth(start.getMonth() - 6)
            break
          case '1Y':
            start.setFullYear(start.getFullYear() - 1)
            break
          default:
            start.setMonth(start.getMonth() - 1)
        }
        const startDayStr = start.toISOString().slice(0, 10)

        const result = await fetchDailyPoolOverviewCTOs(serverApiBaseUrl, restAPIKey, poolAddr, startDayStr, undefined)

        const dailyData = result
          .map((d: any) => ({
            timestamp: d.timestamp,
            value: d.exchangeRate ? Number(d.exchangeRate) / 1e18 : 1.0,
          }))
          .filter((item) => item.timestamp && item.value !== null && item.value !== undefined && !isNaN(item.value))
          .sort((a, b) => a.timestamp - b.timestamp)

        const singleDailyData = aggregateToDaily(dailyData)
        const weeklyData = aggregateToWeekly(singleDailyData)
        setExchangeRateData((prev) => ({ ...prev, [cacheKey]: weeklyData }))
        setExchangeRateGraphData(weeklyData)
      } catch (error) {
        console.error('Error loading exchange rate data:', error)
        setExchangeRateGraphData([])
      }
      setExchangeRateLoading(false)
    }

    loadExchangeRateData()
  }, [activeTab, timeFilter, isApp, serverApiBaseUrl, restAPIKey, poolAddr])

  const _formatHistoricalData = (data: LytHistoryCTO[]) => {
    if (!data || data.length === 0) {
      return []
    }

    const filtered = data
      .filter((item) => {
        return (
          item &&
          item.timestamp &&
          item.value !== undefined &&
          item.value !== null &&
          !isNaN(item.value) &&
          typeof item.value === 'number'
        )
      })
      .sort((a, b) => a.timestamp - b.timestamp)

    const monthlyData = new Map<string, { month: string; value: number; timestamp: number }>()

    filtered.forEach((item) => {
      const date = new Date(item.timestamp * 1000)
      const monthLabel = date.toLocaleDateString('en-US', { month: 'short' })
      const monthKey = `${date.getFullYear()}-${date.getMonth()}`

      if (!monthlyData.has(monthKey)) {
        monthlyData.set(monthKey, {
          month: monthLabel,
          value: item.value,
          timestamp: item.timestamp,
        })
      }
    })

    return Array.from(monthlyData.values()).sort((a, b) => a.timestamp - b.timestamp)
  }

  const _formatDailyHistoricalData = (data: LytHistoryCTO[]) => {
    if (!data || data.length === 0) {
      return []
    }

    const filtered = data
      .filter((item) => {
        return (
          item &&
          item.timestamp &&
          item.value !== undefined &&
          item.value !== null &&
          !isNaN(item.value) &&
          typeof item.value === 'number'
        )
      })
      .sort((a, b) => a.timestamp - b.timestamp)

    const result: Array<{ month: string; value: number; timestamp: number }> = []
    const monthlyData = new Map<string, { value: number; timestamp: number }>()

    filtered.forEach((item) => {
      const date = new Date(item.timestamp * 1000)
      const monthKey = `${date.getFullYear()}-${date.getMonth()}`

      if (!monthlyData.has(monthKey)) {
        monthlyData.set(monthKey, {
          value: item.value,
          timestamp: item.timestamp,
        })
      }
    })

    monthlyData.forEach((data) => {
      const date = new Date(data.timestamp * 1000)
      const monthLabel = date.toLocaleDateString('en-US', { month: 'short' })

      result.push({
        month: monthLabel,
        value: data.value,
        timestamp: data.timestamp,
      })
    })

    return result.sort((a, b) => a.timestamp - b.timestamp)
  }

  if (!isApp) {
    return (
      <Box
        data-testid="swap-home-page"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          padding: '24px',
          backgroundColor: theme.palette.background.canvas,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            gap: 4,
          }}
        >
          <Typography variant="h1">Transparency Dashboard</Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: 2,
            }}
          >
            <Box
              sx={{
                backgroundColor: 'background.paper',
                padding: 2,
                borderRadius: 1,
              }}
            >
              <Typography variant="caption">Current APY</Typography>
              <Typography variant="h4">{displayApyLoading || apy === null ? 'Loading...' : `${apy}%`}</Typography>
            </Box>

            <Box
              sx={{
                backgroundColor: 'background.paper',
                padding: 2,
                borderRadius: 1,
              }}
            >
              <Typography variant="caption">TVL</Typography>
              <Typography variant="h4">{loading ? 'Loading...' : tvl !== null ? `$${tvl}` : 'N/A'}</Typography>
            </Box>

            <Box
              sx={{
                backgroundColor: 'background.paper',
                padding: 2,
                borderRadius: 1,
              }}
            >
              <Typography variant="caption">24h Volume</Typography>
              <Typography variant="h4">
                <Live24hVolume
                  serverApiBaseUrl={serverApiBaseUrl}
                  restAPIKey={restAPIKey}
                  poolAddr={poolAddr}
                  chainConfiguration={chainConfiguration}
                />
              </Typography>
            </Box>

            <Box
              sx={{
                backgroundColor: 'background.paper',
                padding: 2,
                borderRadius: 1,
              }}
            >
              <Typography variant="caption">Exchange Rate</Typography>
              <Typography variant="h4">{loading ? 'Loading...' : tokenPrice !== null ? tokenPrice : 'N/A'}</Typography>
            </Box>
          </Box>

          <LytHistorical
            creationTimestamp={creationTimestamp}
            serverApiBaseUrl={serverApiBaseUrl}
            restAPIKey={restAPIKey}
            poolAddr={poolAddr}
            providerReadOnly={providerReadOnly}
          />

          <LytReserves variant={variant} />
        </Box>
      </Box>
    )
  }

  return (
    <Box
      data-testid="swap-home-page"
      sx={{
        minHeight: '100vh',
        backgroundColor: '#00000f',
      }}
    >
      <Box
        sx={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: { xs: '16px', md: '32px' },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            gap: 4,
          }}
        >
          <Typography variant="h1">Transparency Dashboard</Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: 3,
            }}
          >
            <Box
              sx={{
                backgroundColor: '#1a1a1a',
                padding: 4,
                borderRadius: 2,
                border: '1px solid rgba(255, 255, 255, 0.12)',
              }}
            >
              <Typography variant="h3" sx={{ color: 'white', fontWeight: 600 }}>
                {displayApyLoading || apy === null ? 'Loading...' : `${apy}%`}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: 'rgba(255, 255, 255, 0.6)', mt: 1, display: 'block', fontSize: '13px' }}
              >
                Current APY
              </Typography>
            </Box>

            <Box
              sx={{
                backgroundColor: '#1a1a1a',
                padding: 4,
                borderRadius: 2,
                border: '1px solid rgba(255, 255, 255, 0.12)',
              }}
            >
              <Typography variant="h3" sx={{ color: 'white', fontWeight: 600 }}>
                {loading ? 'Loading...' : marketCap !== null ? marketCap : '$100,000'}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: 'rgba(255, 255, 255, 0.6)', mt: 1, display: 'block', fontSize: '13px' }}
              >
                Market Cap
              </Typography>
            </Box>

            <Box
              sx={{
                backgroundColor: '#1a1a1a',
                padding: 4,
                borderRadius: 2,
                border: '1px solid rgba(255, 255, 255, 0.12)',
              }}
            >
              <Typography variant="h3" sx={{ color: 'white', fontWeight: 600 }}>
                <Live24hVolume
                  serverApiBaseUrl={serverApiBaseUrl}
                  restAPIKey={restAPIKey}
                  poolAddr={poolAddr}
                  chainConfiguration={chainConfiguration}
                />
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: 'rgba(255, 255, 255, 0.6)', mt: 1, display: 'block', fontSize: '13px' }}
              >
                24h Volume
              </Typography>
            </Box>

            <Box
              sx={{
                backgroundColor: '#1a1a1a',
                padding: 4,
                borderRadius: 2,
                border: '1px solid rgba(255, 255, 255, 0.12)',
              }}
            >
              <Typography variant="h3" sx={{ color: 'white', fontWeight: 600 }}>
                {loading ? 'Loading...' : tokenPrice !== null ? tokenPrice : '$1.000000'}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: 'rgba(255, 255, 255, 0.6)', mt: 1, display: 'block', fontSize: '13px' }}
              >
                Market Price
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', mb: 2 }}>
            <Typography variant="h2" sx={{ color: 'white' }}>
              Historical Performance
            </Typography>
          </Box>
          <Box
            sx={{
              backgroundColor: '#1a1a1a',
              padding: 3,
              borderRadius: 2,
              border: '1px solid #555',
              maxWidth: '100%',
              overflow: 'hidden',
            }}
          >
            <Box sx={{ mb: 3 }}>
              <Box
                sx={{
                  display: 'inline-flex',
                  gap: 0.5,
                  mb: 2,
                  backgroundColor: '#0a1929',
                  borderRadius: 1.5,
                  p: 0.5,
                }}
              >
                <Box
                  onClick={() => setActiveTab('apy')}
                  sx={{
                    borderRadius: 1,
                    px: 3,
                    py: 1.5,
                    fontWeight: 600,
                    color: activeTab === 'apy' ? '#000' : 'rgba(255, 255, 255, 0.7)',
                    backgroundColor: activeTab === 'apy' ? '#d95f80' : 'transparent',
                    textTransform: 'none',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    fontSize: '14px',
                    '&:hover': {
                      backgroundColor: activeTab === 'apy' ? '#d95f80' : 'rgba(255, 255, 255, 0.05)',
                    },
                  }}
                >
                  APY
                </Box>
                <Box
                  onClick={() => setActiveTab('asset')}
                  sx={{
                    borderRadius: 1,
                    px: 3,
                    py: 1.5,
                    fontWeight: 600,
                    color: activeTab === 'asset' ? '#000' : 'rgba(255, 255, 255, 0.7)',
                    backgroundColor: activeTab === 'asset' ? '#42EBBD' : 'transparent',
                    textTransform: 'none',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    fontSize: '14px',
                    '&:hover': {
                      backgroundColor: activeTab === 'asset' ? '#42EBBD' : 'rgba(255, 255, 255, 0.05)',
                    },
                  }}
                >
                  Reserves APY
                </Box>
                <Box
                  onClick={() => setActiveTab('exchange')}
                  sx={{
                    borderRadius: 1,
                    px: 3,
                    py: 1.5,
                    fontWeight: 600,
                    color: activeTab === 'exchange' ? '#000' : 'rgba(255, 255, 255, 0.7)',
                    backgroundColor: activeTab === 'exchange' ? '#1EAEDB' : 'transparent',
                    textTransform: 'none',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    fontSize: '14px',
                    '&:hover': {
                      backgroundColor: activeTab === 'exchange' ? '#1EAEDB' : 'rgba(255, 255, 255, 0.05)',
                    },
                  }}
                >
                  Exchange Rate
                </Box>
              </Box>
            </Box>
            <Box
              sx={{
                height: 300,
                mb: 4,
                overflow: 'hidden',
                position: 'relative',
                '& > div': {
                  maxWidth: '100%',
                  overflow: 'hidden',
                },
                '@media (max-width: 1200px)': {
                  '& svg': {
                    transform: 'scale(0.8)',
                    transformOrigin: 'top left',
                  },
                  height: 240,
                },
                '@media (max-width: 900px)': {
                  '& svg': {
                    transform: 'scale(0.6)',
                    transformOrigin: 'top left',
                  },
                  height: 180,
                },
              }}
            >
              {activeTab === 'apy' && (
                <>
                  {displayApyLoading ? (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 2,
                        height: 250,
                        justifyContent: 'center',
                      }}
                    >
                      <CircularProgress sx={{ color: '#d95f80' }} />
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        Loading Historical APY
                      </Typography>
                    </Box>
                  ) : displayGraphData.length > 0 ? (
                    <LineChart
                      width={1100}
                      height={250}
                      series={[
                        {
                          data: displayGraphData.map((d) => d.value),
                          label: 'APY',
                          color: '#d95f80',
                          area: true,
                          showMark: true,
                          highlightScope: { highlight: 'item', fade: 'global' },
                          curve: 'linear',
                          connectNulls: true,
                          valueFormatter: (value: number | null, context: any) => {
                            const dataIndex = context?.dataIndex ?? 0
                            const monthLabel = displayGraphData[dataIndex]?.month || 'Unknown'
                            return `${monthLabel}: ${value?.toFixed(2)}%`
                          },
                        },
                      ]}
                      xAxis={[
                        {
                          scaleType: 'point',
                          data: displayGraphData.map((d) => d.month),
                          tickLabelStyle: { fill: '#888', fontSize: 12 },
                          valueFormatter: (value: string) => value,
                        },
                      ]}
                      yAxis={[
                        {
                          tickLabelStyle: { fill: '#888', fontSize: 12 },
                          valueFormatter: (value: number | null) => `${value?.toFixed(2)}%`,
                        },
                      ]}
                      grid={{ horizontal: true, vertical: true }}
                      axisHighlight={{ x: 'line', y: 'line' }}
                      disableAxisListener={false}
                      slotProps={{
                        tooltip: {
                          trigger: 'item',
                        },
                      }}
                      sx={{
                        '& .MuiLineElement-root': {
                          strokeWidth: 2,
                        },
                        '& .MuiMarkElement-root': {
                          fill: '#d95f80',
                          r: 3,
                          strokeWidth: 0,
                        },
                        '& .MuiHighlightElement-root': {
                          fill: '#d95f80',
                          r: 5,
                        },
                        '& .MuiAreaElement-root': {
                          fill: '#d95f80',
                          opacity: 0.2,
                        },
                        '& .MuiChartsGrid-line': {
                          stroke: '#333',
                          strokeDasharray: '3 3',
                        },
                        '& .MuiChartsAxis-line': {
                          stroke: '#444',
                        },
                        '& .MuiChartsAxis-tick': {
                          stroke: '#444',
                        },
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: 250,
                        color: '#888',
                      }}
                    >
                      <Typography variant="body1">No data available for this time period</Typography>
                    </Box>
                  )}
                </>
              )}

              {activeTab === 'asset' && (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                    <FormControl size="small">
                      <Select
                        value={selectedAsset}
                        onChange={(e) => setSelectedAsset(e.target.value)}
                        sx={{
                          backgroundColor: '#041020',
                          color: 'white',
                          border: '1px solid #555',
                          borderRadius: 1,
                          '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                          '& .MuiSelect-icon': { color: 'white' },
                          fontSize: '14px',
                          minWidth: '200px',
                        }}
                      >
                        {reserveAssetsWithPercentage.map((asset) => (
                          <MenuItem
                            key={asset.name}
                            value={asset.name}
                            sx={{ backgroundColor: '#1a1a1a', color: 'white' }}
                          >
                            {asset.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                  {assetApyLoading ? (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 2,
                        height: 250,
                        justifyContent: 'center',
                      }}
                    >
                      <CircularProgress sx={{ color: '#42EBBD' }} />
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        Loading Historical Reserves APY
                      </Typography>
                    </Box>
                  ) : displayGraphData.length > 0 ? (
                    <LineChart
                      width={1100}
                      height={250}
                      series={[
                        {
                          data: assetApyGraphData
                            .filter((_d, index) => {
                              const firstNonZeroIndex = assetApyGraphData.findIndex((item) => item.value > 0)
                              return index >= firstNonZeroIndex && firstNonZeroIndex !== -1
                            })
                            .map((d) => d.value),
                          label: `${selectedAsset} APY`,
                          color: '#42EBBD',
                          area: true,
                          showMark: true,
                          highlightScope: { highlight: 'item', fade: 'global' },
                          curve: 'linear',
                          connectNulls: true,
                          valueFormatter: (value: number | null, context: any) => {
                            const dataIndex = context?.dataIndex ?? 0
                            const monthLabel = assetApyGraphData[dataIndex]?.month || 'Unknown'
                            return `${monthLabel}: ${value?.toFixed(2)}%`
                          },
                        },
                      ]}
                      xAxis={[
                        {
                          scaleType: 'point',
                          data: assetApyGraphData
                            .filter((_d, index) => {
                              const firstNonZeroIndex = assetApyGraphData.findIndex((item) => item.value > 0)
                              return index >= firstNonZeroIndex && firstNonZeroIndex !== -1
                            })
                            .map((d) => d.month),
                          tickLabelStyle: { fill: '#888', fontSize: 12 },
                        },
                      ]}
                      yAxis={[
                        {
                          tickLabelStyle: { fill: '#888', fontSize: 12 },
                          valueFormatter: (value: number | null) => `${value?.toFixed(2)}%`,
                        },
                      ]}
                      grid={{ horizontal: true, vertical: true }}
                      axisHighlight={{ x: 'line', y: 'line' }}
                      disableAxisListener={false}
                      slotProps={{
                        tooltip: {
                          trigger: 'item',
                        },
                      }}
                      sx={{
                        '& .MuiLineElement-root': {
                          strokeWidth: 2,
                        },
                        '& .MuiMarkElement-root': {
                          fill: '#42EBBD',
                          r: 3,
                          strokeWidth: 0,
                        },
                        '& .MuiHighlightElement-root': {
                          fill: '#42EBBD',
                          r: 5,
                        },
                        '& .MuiAreaElement-root': {
                          fill: '#42EBBD',
                          opacity: 0.2,
                        },
                        '& .MuiChartsGrid-line': {
                          stroke: '#333',
                          strokeDasharray: '3 3',
                        },
                        '& .MuiChartsAxis-line': {
                          stroke: '#444',
                        },
                        '& .MuiChartsAxis-tick': {
                          stroke: '#444',
                        },
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: 250,
                        color: '#888',
                      }}
                    >
                      <Typography variant="body1">No data available for this time period</Typography>
                    </Box>
                  )}
                </>
              )}

              {activeTab === 'exchange' && (
                <>
                  {exchangeRateLoading ? (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 2,
                        height: 250,
                        justifyContent: 'center',
                      }}
                    >
                      <CircularProgress sx={{ color: '#1EAEDB' }} />
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        Loading Historical Exchange Rate
                      </Typography>
                    </Box>
                  ) : exchangeRateGraphData.length > 0 ? (
                    <LineChart
                      width={1100}
                      height={250}
                      series={[
                        {
                          data: exchangeRateGraphData.map((d) => d.value),
                          label: 'Exchange Rate',
                          color: '#1EAEDB',
                          area: true,
                          showMark: true,
                          highlightScope: { highlight: 'item', fade: 'global' },
                          curve: 'linear',
                          connectNulls: true,
                          valueFormatter: (value: number | null, context: any) => {
                            const dataIndex = context?.dataIndex ?? 0
                            const monthLabel = exchangeRateGraphData[dataIndex]?.month || 'Unknown'
                            return `${monthLabel}: $${value?.toFixed(6)}`
                          },
                        },
                      ]}
                      xAxis={[
                        {
                          scaleType: 'point',
                          data: exchangeRateGraphData.map((d) => d.month),
                          tickLabelStyle: { fill: '#888', fontSize: 12 },
                          valueFormatter: (value: string) => value,
                        },
                      ]}
                      yAxis={[
                        {
                          tickLabelStyle: { fill: '#888', fontSize: 12 },
                          valueFormatter: (value: number | null) => `$${value?.toFixed(6)}`,
                        },
                      ]}
                      grid={{ horizontal: true, vertical: true }}
                      axisHighlight={{ x: 'line', y: 'line' }}
                      disableAxisListener={false}
                      slotProps={{
                        tooltip: {
                          trigger: 'item',
                        },
                      }}
                      sx={{
                        '& .MuiLineElement-root': {
                          strokeWidth: 2,
                        },
                        '& .MuiMarkElement-root': {
                          fill: '#1EAEDB',
                          r: 3,
                          strokeWidth: 0,
                        },
                        '& .MuiHighlightElement-root': {
                          fill: '#1EAEDB',
                          r: 5,
                        },
                        '& .MuiAreaElement-root': {
                          fill: '#1EAEDB',
                          opacity: 0.2,
                        },
                        '& .MuiChartsGrid-line': {
                          stroke: '#333',
                          strokeDasharray: '3 3',
                        },
                        '& .MuiChartsAxis-line': {
                          stroke: '#444',
                        },
                        '& .MuiChartsAxis-tick': {
                          stroke: '#444',
                        },
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: 250,
                        color: '#888',
                      }}
                    >
                      <Typography variant="body1">No data available for this time period</Typography>
                    </Box>
                  )}
                </>
              )}
            </Box>

            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
              {(['1M', '3M', '6M', '1Y'] as const).map((period) => (
                <Box
                  key={period}
                  onClick={() => setTimeFilter(period)}
                  sx={{
                    px: 2.5,
                    py: 1,
                    borderRadius: 1,
                    backgroundColor: timeFilter === period ? '#d95f80' : 'transparent',
                    color: timeFilter === period ? 'white' : 'rgba(255, 255, 255, 0.7)',
                    fontSize: '13px',
                    fontWeight: timeFilter === period ? 600 : 400,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    border: '1px solid',
                    borderColor: timeFilter === period ? '#d95f80' : 'rgba(255, 255, 255, 0.2)',
                    '&:hover': {
                      color: 'white',
                      backgroundColor: timeFilter === period ? '#d95f80' : 'rgba(255, 255, 255, 0.1)',
                      borderColor: timeFilter === period ? '#d95f80' : 'rgba(255, 255, 255, 0.4)',
                    },
                  }}
                >
                  {period}
                </Box>
              ))}
            </Box>
          </Box>

          <Typography variant="h2" sx={{ color: 'white', mb: 2, mt: 6 }}>
            Reserves
          </Typography>
          <LytReserves variant={variant} />

          <Typography variant="h2" sx={{ color: 'white', mb: 2, mt: 8 }}>
            Reserve Reports
          </Typography>
          <LytReserveReports />
        </Box>

        <Typography variant="h2" sx={{ color: 'white', mb: 2, mt: 8 }}>
          FAQs
        </Typography>
        <Box
          sx={{
            backgroundColor: '#1a1a1a',
            padding: 4,
            borderRadius: 2,
            border: '1px solid rgba(255, 255, 255, 0.12)',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            <Accordion
              expanded={expandedFaq === 'faq1'}
              onChange={() => setExpandedFaq(expandedFaq === 'faq1' ? false : 'faq1')}
              sx={{
                backgroundColor: 'transparent',
                border: 'none',
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                '&:before': { display: 'none' },
                '&:last-child': { borderBottom: 'none' },
                borderRadius: 0,
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />} sx={{ py: 3, px: 4 }}>
                <Typography variant="body1" sx={{ color: 'white', fontWeight: 500 }}>
                  What is the Sierra protocol and the SIERRA token?
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 4, pb: 3 }}>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', lineHeight: 1.6 }}>
                  The Sierra protocol issues and manages the exchange rate of the SIERRA token. The SIERRA token is a
                  Liquid Yield Token (LYT), which provides yield to tokenholders. The underlying yield is generated from
                  reserves deployed into yield generating strategies backed by investment-grade Real World Assets and
                  blue-chip DeFi protocols. Learn more:{' '}
                  <Link
                    href="https://docs.sierra.money/overview/introduction"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ color: '#E769A6', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                  >
                    Introduction to Sierra
                  </Link>
                </Typography>
              </AccordionDetails>
            </Accordion>

            <Accordion
              expanded={expandedFaq === 'faq2'}
              onChange={() => setExpandedFaq(expandedFaq === 'faq2' ? false : 'faq2')}
              sx={{
                backgroundColor: 'transparent',
                border: 'none',
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                '&:before': { display: 'none' },
                '&:last-child': { borderBottom: 'none' },
                borderRadius: 0,
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />} sx={{ py: 3, px: 4 }}>
                <Typography variant="body1" sx={{ color: 'white', fontWeight: 500 }}>
                  How is the APY calculated and what factors affect it?
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 4, pb: 3 }}>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', lineHeight: 1.6 }}>
                  The APY is calculated based on the weighted average yield of the diversified reserve portfolio backing
                  SIERRA. It fluctuates based on:
                  <br />
                  <br />
                  Market conditions
                  <br />
                  Performance of underlying assets
                  <br />
                  The composition of reserves
                  <br />
                  <br />
                  The APY calculations update in real time to reflect current market conditions. Learn more:{' '}
                  <Link
                    href="https://docs.sierra.money/overview/yield-generation-and-accrual"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ color: '#E769A6', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                  >
                    Yield Generation
                  </Link>
                </Typography>
              </AccordionDetails>
            </Accordion>

            <Accordion
              expanded={expandedFaq === 'faq3'}
              onChange={() => setExpandedFaq(expandedFaq === 'faq3' ? false : 'faq3')}
              sx={{
                backgroundColor: 'transparent',
                border: 'none',
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                '&:before': { display: 'none' },
                '&:last-child': { borderBottom: 'none' },
                borderRadius: 0,
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />} sx={{ py: 3, px: 4 }}>
                <Typography variant="body1" sx={{ color: 'white', fontWeight: 500 }}>
                  Who manages SIERRA&apos;s reserves?
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 4, pb: 3 }}>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', lineHeight: 1.6 }}>
                  SIERRA&apos;s reserves are managed through OpenTrade, an institutional-grade
                  &quot;yield-as-a-service&quot; provider backed by Mercury, Notion Capital, a16z, Circle Ventures and
                  other top venture firms. Additionally, the Sierra Foundation has an Advisory Council, composed of
                  experts in stablecoins, DeFi, traditional asset management and risk modelling to help guide setting
                  and updating the Reserve Strategy. Learn More:{' '}
                  <Link
                    href="https://docs.sierra.money/reserves-management/reserve-strategy"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ color: '#E769A6', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                  >
                    Reserve Strategy
                  </Link>
                </Typography>
              </AccordionDetails>
            </Accordion>

            <Accordion
              expanded={expandedFaq === 'faq4'}
              onChange={() => setExpandedFaq(expandedFaq === 'faq4' ? false : 'faq4')}
              sx={{
                backgroundColor: 'transparent',
                border: 'none',
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                '&:before': { display: 'none' },
                '&:last-child': { borderBottom: 'none' },
                borderRadius: 0,
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />} sx={{ py: 3, px: 4 }}>
                <Typography variant="body1" sx={{ color: 'white', fontWeight: 500 }}>
                  What types of yield sources can the reserves be deployed into?
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 4, pb: 3 }}>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', lineHeight: 1.6 }}>
                  SIERRA&apos;s reserves are allocated to OpenTrade&apos;s RWA and DeFi vaults backed by Fidelity&apos;s
                  U.S. Treasury Money Market Fund, as well as AAVE, Morpho and Wildcat (only Wintermute). Additional
                  yield sources will be added in adherence to Sierra&apos;s Risk Framework. Learn More:{' '}
                  <Link
                    href="https://docs.sierra.money/overview/yield-generation-and-accrual"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ color: '#E769A6', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                  >
                    Yield Generation
                  </Link>
                </Typography>
              </AccordionDetails>
            </Accordion>

            <Accordion
              expanded={expandedFaq === 'faq5'}
              onChange={() => setExpandedFaq(expandedFaq === 'faq5' ? false : 'faq5')}
              sx={{
                backgroundColor: 'transparent',
                border: 'none',
                '&:before': { display: 'none' },
                '&:last-child': { borderBottom: 'none' },
                borderRadius: 0,
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />} sx={{ py: 3, px: 4 }}>
                <Typography variant="body1" sx={{ color: 'white', fontWeight: 500 }}>
                  Where can I observe SIERRA&apos;s reserves onchain?
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 4, pb: 3 }}>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', lineHeight: 1.6 }}>
                  Sierra&apos;s Issuer Vault holds the reserve assets backing the SIERRA token. The reserve assets held
                  in the Issuer Vault include USDC and Vault Tokens issued by OpenTrade, which generate yield backed by
                  RWA and DeFi assets. You can observe the Issuer Vault&apos;s portfolio of reserves here:{' '}
                  <Link
                    href="https://debank.com/profile/0xbc7c0b1b9c61f35068561077fbaa163707128597"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ color: '#E769A6', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                  >
                    https://debank.com/profile/0xbc7c0b1b9c61f35068561077fbaa163707128597
                  </Link>
                </Typography>
              </AccordionDetails>
            </Accordion>

            <Accordion
              expanded={expandedFaq === 'faq6'}
              onChange={() => setExpandedFaq(expandedFaq === 'faq6' ? false : 'faq6')}
              sx={{
                backgroundColor: 'transparent',
                border: 'none',
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                '&:before': { display: 'none' },
                '&:last-child': { borderBottom: 'none' },
                borderRadius: 0,
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />} sx={{ py: 3, px: 4 }}>
                <Typography variant="body1" sx={{ color: 'white', fontWeight: 500 }}>
                  Are there other ways to verify reserves?
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 4, pb: 3 }}>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', lineHeight: 1.6 }}>
                  Our infrastructure partner OpenTrade provides weekly reserve attestation reports conducted by a 3rd
                  party, FCA-regulated asset manager. These reports will be easily accessible through Sierra&apos;s
                  transparency page.
                </Typography>
              </AccordionDetails>
            </Accordion>

            <Accordion
              expanded={expandedFaq === 'faq7'}
              onChange={() => setExpandedFaq(expandedFaq === 'faq7' ? false : 'faq7')}
              sx={{
                backgroundColor: 'transparent',
                border: 'none',
                '&:before': { display: 'none' },
                '&:last-child': { borderBottom: 'none' },
                borderRadius: 0,
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />} sx={{ py: 3, px: 4 }}>
                <Typography variant="body1" sx={{ color: 'white', fontWeight: 500 }}>
                  Have Sierra&apos;s contracts been audited?
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 4, pb: 3 }}>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', lineHeight: 1.6 }}>
                  The underlying vault contracts as well as Sierra&apos;s LYT token contract have been audited by top
                  security group Spearbit Cantina:
                  <br />
                  <br />
                  <Link
                    href="https://cantina.xyz/portfolio/967eeb2b-b6f6-4b17-b819-9bfb450876ca"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ color: '#E769A6', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                  >
                    https://cantina.xyz/portfolio/967eeb2b-b6f6-4b17-b819-9bfb450876ca
                  </Link>
                  <br />
                  <br />
                  <Link
                    href="https://docs.opentrade.io/code-audits"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ color: '#E769A6', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                  >
                    https://docs.opentrade.io/code-audits
                  </Link>
                </Typography>
              </AccordionDetails>
            </Accordion>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
